/* eslint-disable no-useless-rename */

import { Loader, ToastProvider, useModal } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { ActivitiesService, UserService } from 'gw-capability-gateway';
import { useActivityUtil } from 'ent-capability-hooks';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import gatewayMessages from 'gw-capability-gateway-react/gateway.messages';
import ActivityTableComponent from '../ActivityTableComponent/ActivityTableComponent';
import AddActivityPopUp from './AddActivityPopUp';
import messages from './OpenActivitiesComponent.messages';
import metadata from './OpenActivitiesComponent.metadata.json5';
import styles from './OpenActivitiesComponent.module.scss';

function OpenActivitiesComponent(props) {
    const { showAlert, showModal } = useModal();
    const { activitiesTomorrowData, 
            activitiesFutureData, 
            activitiesThisWeekData, 
            activitiesTodayData, 
            activitiesOverdueData, filterActivitiesByValue, filterActivitiesByStatus } = useActivityUtil();
   
    const {
        authHeader,
        getActivities,
        activityEntityId,
        activityEntity,
        createNewActivity,
        getPolicyDetails,
        getActivitiesTileUpdatedCount,
        viewModelService,
    } = props;
    const translator = useTranslator();
    const [openActivityResponse, updateOpenActivityResponse] = useState([]);
    const [activityList, setActivityList] = useState([]);
    const [activityFilter, setActivityFilter] = useState({
        statusFilter: '',
        searchFilter: '',
    });
    const [displayNoActivities, updateDisplayNoActivities] = useState(false);
    const [activityOptions, updateActivityOptions] = useState();
    const [showActivityButton, updateShowActivityButton] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [publicId, setPublicId] = useState('');

    const splitActivitiesForTable = useCallback((activitiesList) => ({activitiesComplete: activitiesList.filter(activitiesInfo => activitiesInfo.status === 'complete'),
        activitiesFuture: activitiesFutureData(activitiesList, 'open, unassigned'),
        activitiesOverdue: activitiesOverdueData(activitiesList, 'open, unassigned'),
        activitiesThisWeek: activitiesThisWeekData(activitiesList, 'open, unassigned'),
        activitiesToday: activitiesTodayData(activitiesList, 'open, unassigned'),
        activitiesTomorrow: activitiesTomorrowData(activitiesList, 'open, unassigned')}), [activitiesFutureData, activitiesOverdueData, activitiesThisWeekData, activitiesTodayData, activitiesTomorrowData])

    const getActivityOptions = useCallback(() => {
        const openActivityOptionsArray = [
            translator(messages.allOpen),
            translator(messages.allCompleted),
            translator(messages.overdue),
            translator(messages.openAssignedToMe),
        ];
        
        return openActivityOptionsArray.map(key => ({
                code: key,
                name: key,
            }));
    }, [translator]);

    const getActivityDataTable = useCallback(
        (activityResponseData, filter) => {
            let updatedActivityList = activityResponseData;
            const statusMap = {
                allOpen: translator(messages.allOpen),
                overDueOnly: translator(messages.overdue),
                allClose: translator(messages.allCompleted),
                openAssignedToMe: translator(messages.openAssignedToMe),
            };

            if (_.toString(filter.statusFilter) !== '') {
                updatedActivityList = filterActivitiesByStatus(
                    filter.statusFilter,
                    statusMap,
                    updatedActivityList
                );
            }

            if (_.toString(filter.searchFilter) !== '') {
                updatedActivityList = filterActivitiesByValue(
                    updatedActivityList,
                    filter.searchFilter
                );
            }

            setActivityList(_.sortBy(updatedActivityList, 'dueDate'));
            setActivityFilter(filter);

            if (!updatedActivityList.length) {
                updateDisplayNoActivities(true);
            } else {
                updateDisplayNoActivities(false);
            }
        },
        [filterActivitiesByStatus, filterActivitiesByValue, translator]
    );

    const responseData = useCallback(async () => {
        setIsLoading(true);

        const filter = _.cloneDeep(activityFilter);

        filter.statusFilter = translator(messages.allOpen);

        const response = await getActivities();
        
        const activities = _.map(response, activity => _.extend({}, activity, { expanded: false }));

        getActivityDataTable(activities, filter);
        updateOpenActivityResponse(activities);
        updateActivityOptions(getActivityOptions(activities));

        return true;
    }, [
        activityFilter,
        getActivities,
        getActivityDataTable,
        getActivityOptions,
        translator,
    ]);

    const shouldShowAddActivityButton = useCallback(() => {
        const permissionDTO = {
            permission: 'actcreate',
        };

        UserService.hasUserSystemPermission(permissionDTO, authHeader).then(
            showActivity => {
                updateShowActivityButton(showActivity);
                setIsLoading(false);
            }
        );
    }, [authHeader]);

    useEffect(() => {
        responseData();
        shouldShowAddActivityButton();
        // only execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const createNote = async (data, publicID) => {
        const activityNoteData = await ActivitiesService.createNoteForActivity(
            publicID,
            data,
            authHeader
        );

        if (!_.isUndefined(activityNoteData)) {
            showAlert({
                title: gatewayMessages.noteCreated,
                message: gatewayMessages.noteCreatedSuccessfully,
                status: 'success',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok,
            }).then(() => {
                const activityObj = openActivityResponse.find(
                    obj => obj.publicID === publicID
                );

                activityObj.notes.push(activityNoteData);
                getActivityDataTable(openActivityResponse, activityFilter);
            }, _.noop);
        }
    };

    const handleFilterChange = (value, id) => {
        const filter = _.cloneDeep(activityFilter);

        if (id === 'statusFilter') {
            filter.statusFilter = value;
            getActivityDataTable(openActivityResponse, filter);
        }

        if (id === 'searchFilter') {
            filter.searchFilter = value;
            getActivityDataTable(openActivityResponse, filter);
        }
    };

    const handleError = useCallback((title, message) => showAlert({
            title,
            message,
            status: 'error',
            icon: 'mi-error-outline',
            confirmButtonText: messages.noTypeSelected,
        }).catch(_.noop), [showAlert]);

    const addNewActivityAddClick = useCallback(
        async model => {
            let newActivityData = {
                ..._.omit(model.value, 'assignedTo'),
                selectedAssignee: _.get(model.value, 'assignedTo'),
                activityPattern: {
                    code: _.get(model.value, 'code'),
                    priority: _.get(model.value, 'priority'),
                },
            };

            newActivityData = _.omit(newActivityData, 'jobType');

            switch (activityEntity) {
                case 'account':
                    newActivityData.accountNumber = activityEntityId;
                    break;
                case 'policy':
                    newActivityData.policyNumber = activityEntityId;
                    break;
                case 'job':
                    newActivityData.jobNumber = activityEntityId;
                    break;
                default:
                    break;
            }

            if (!_.get(model.value, 'jobType')) {
                handleError(
                    messages.noActivityType,
                    messages.noActivityTypeMessage
                );
            } else {
                const addResponse = await createNewActivity(newActivityData);

                if (!_.isEmpty(addResponse) && !_.isUndefined(addResponse)) {
                    ToastProvider.toast({
                        type: 'success',
                        message: translator(messages.activityCreated),
                        autoClose: true,
                    });

                    if (getActivitiesTileUpdatedCount) {
                        getActivitiesTileUpdatedCount();
                    }
                }

                const updatedActivityResponseArray = [
                    ...openActivityResponse,
                    addResponse,
                ];

                updateOpenActivityResponse(updatedActivityResponseArray);

                const filter = _.cloneDeep(activityFilter);

                filter.statusFilter = translator(messages.allOpen);
                getActivityDataTable(updatedActivityResponseArray, filter);
                updateActivityOptions(
                    getActivityOptions(updatedActivityResponseArray)
                );
                
                if (!_.isUndefined(getPolicyDetails)) {
                    getPolicyDetails();
                }
            }
        },
        [
            openActivityResponse,
            activityEntity,
            activityEntityId,
            activityFilter,
            createNewActivity,
            getActivitiesTileUpdatedCount,
            getActivityDataTable,
            getActivityOptions,
            getPolicyDetails,
            handleError,
            translator,
        ]
    );

    const addNewActivityButtonClick = useCallback(() => {
        showModal(
            <AddActivityPopUp
                title={translator(messages.addActivity)}
                actionBtnLabel={messages.createActivity}
                cancelBtnLabel={messages.cancel}
                activityEntity={activityEntity}
                activityEntityId={activityEntityId}
                viewModelService={viewModelService}
                authHeader={authHeader}
            />
        )
            .then(addNewActivityAddClick)
            .catch(_.noop);
    }, [showModal, translator, activityEntity, activityEntityId, viewModelService, authHeader, addNewActivityAddClick]);

    const onClickComplete = useCallback(
        data => {
            const activityObj = openActivityResponse.find(
                obj => obj.publicID === data
            );

            ActivitiesService.markActivityAsCompleted(data, authHeader).then(
                completeActivity => {
                    if (activityObj) {
                        activityObj.status = completeActivity.status;
                        activityObj.closeDate = completeActivity.closeDate;
                        activityObj.completedDate =
                            completeActivity.completedDate;
                        activityObj.canComplete = false;
                    }
                    
                    setPublicId(data);
                }
            );
        },
        [authHeader, openActivityResponse]
    );

    const {
        activitiesOverdue: activitiesOverdue,
        activitiesComplete: activitiesComplete,
        activitiesFuture: activitiesFuture,
        activitiesThisWeek: activitiesThisWeek,
        activitiesToday: activitiesToday,
        activitiesTomorrow: activitiesTomorrow,
    } = splitActivitiesForTable(activityList);

    const overrideProps = {
        statusFilter: {
            availableValues: activityOptions,
            value: activityFilter.statusFilter,
        },
        searchFilter: {
            value: activityFilter.searchFilter,
        },
        noActivityFound: {
            visible: displayNoActivities !== false,
        },
        noActivitySeparator: {
            visible: displayNoActivities !== false,
        },
        activitiesDetailGrid: {
            visible: displayNoActivities === false,
        },
        addActivityButton: {
            visible: showActivityButton,
        },
        activitiesTableContainer: {
            activitiesOverdue,
            activitiesToday,
            activitiesTomorrow,
            activitiesThisWeek,
            activitiesFuture,
            activitiesComplete,
            displayNoActivities,
            createNote,
            onClickComplete,
            publicId,
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            activitytablecomponent: ActivityTableComponent,
        },
        resolveCallbackMap: {
            handleStatusValueChange: value =>
                handleFilterChange(value, 'statusFilter'),
            handleSearchValueChange: value =>
                handleFilterChange(value, 'searchFilter'),
            addNewActivityClick: addNewActivityButtonClick,
        },
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    return (
        <MetadataContent
            uiProps={metadata.pageContent}
            overrideProps={overrideProps}
            {...resolvers}
        />
    );
}

OpenActivitiesComponent.propTypes = {
    authHeader: PropTypes.shape({}).isRequired,
    getActivities: PropTypes.func.isRequired,
    activityEntityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    activityEntity: PropTypes.string.isRequired,
    createNewActivity: PropTypes.func.isRequired,
    getPolicyDetails: PropTypes.func,
    getActivitiesTileUpdatedCount: PropTypes.func,
};

OpenActivitiesComponent.defaultProps = {
    getActivitiesTileUpdatedCount: undefined,
    getPolicyDetails: undefined,
};

export default withAuthenticationContext(
    withViewModelService(OpenActivitiesComponent)
);
